/**
 * Incident Report Tool - Structured Data Collection
 * 
 * This tool collects comprehensive incident report information including
 * incident details and reporter information. This is an example of a
 * data collection tool that could be used in various scenarios.
 */

import { ToolSchema, ToolImplementation, ToolResult } from '../tool-types';

/**
 * Incident report data structure
 */
interface IncidentReport {
  incident: {
    date: string;
    location: string;
    description: string;
    caused_by: string;
  };
  reporter: {
    first_name: string;
    last_name: string;
    telephone: string;
    email: string;
    postal_address: string;
    police_report: string;
    witnesses: string;
  };
}

/**
 * Tool schema for the incident report tool
 */
export const incidentReportToolSchema: ToolSchema = {
  type: 'function',
  name: 'collect_incident_report',
  description:
    'Collect incident report information including incident details and reporter information. ' +
    'This tool gathers comprehensive information about an incident for documentation and processing.',
  parameters: {
    type: 'object',
    properties: {
      incident: {
        type: 'object',
        description: 'Details about the incident',
        properties: {
          date: {
            type: 'string',
            description: 'Date of the incident'
          },
          location: {
            type: 'string',
            description: 'Location details where the incident occurred'
          },
          description: {
            type: 'string',
            description: 'Description of how the incident happened'
          },
          caused_by: {
            type: 'string',
            description: "Who caused the incident: 'You', 'Other party', or 'Both'",
            enum: ['You', 'Other party', 'Both']
          }
        },
        required: ['date', 'location', 'description', 'caused_by'],
        additionalProperties: false
      },
      reporter: {
        type: 'object',
        description: 'Information about the person reporting the incident',
        properties: {
          first_name: {
            type: 'string',
            description: "Reporter's first name"
          },
          last_name: {
            type: 'string',
            description: "Reporter's last name"
          },
          telephone: {
            type: 'string',
            description: "Reporter's telephone number"
          },
          email: {
            type: 'string',
            description: "Reporter's email address"
          },
          postal_address: {
            type: 'string',
            description: "Reporter's postal address"
          },
          police_report: {
            type: 'string',
            description: "Whether a police report was filed: 'Yes' or 'No'",
            enum: ['Yes', 'No']
          },
          witnesses: {
            type: 'string',
            description: "Whether there were witnesses: 'Yes' or 'No'",
            enum: ['Yes', 'No']
          }
        },
        required: [
          'first_name',
          'last_name',
          'telephone',
          'email',
          'postal_address',
          'police_report',
          'witnesses'
        ],
        additionalProperties: false
      }
    },
    required: ['incident', 'reporter'],
    additionalProperties: false
  }
};

/**
 * Validate incident report data
 */
function validateIncidentReport(data: any): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  // Validate incident
  if (!data.incident) {
    errors.push('Missing incident data');
  } else {
    if (!data.incident.date) errors.push('Missing incident date');
    if (!data.incident.location) errors.push('Missing incident location');
    if (!data.incident.description) errors.push('Missing incident description');
    if (!data.incident.caused_by) errors.push('Missing incident caused_by');
  }

  // Validate reporter
  if (!data.reporter) {
    errors.push('Missing reporter data');
  } else {
    if (!data.reporter.first_name) errors.push('Missing reporter first name');
    if (!data.reporter.last_name) errors.push('Missing reporter last name');
    if (!data.reporter.telephone) errors.push('Missing reporter telephone');
    if (!data.reporter.email) errors.push('Missing reporter email');
    if (!data.reporter.postal_address) errors.push('Missing reporter postal address');
    if (!data.reporter.police_report) errors.push('Missing police report status');
    if (!data.reporter.witnesses) errors.push('Missing witnesses status');
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Tool implementation for the incident report tool
 */
export const incidentReportToolImplementation: ToolImplementation = async (
  args: any
): Promise<ToolResult> => {
  try {
    const { incident, reporter } = args;

    // Validate the data
    const validation = validateIncidentReport(args);
    if (!validation.valid) {
      console.error('❌ Invalid incident report data:', validation.errors);
      return {
        success: false,
        error: `Invalid incident report data: ${validation.errors.join(', ')}`
      };
    }

    console.log(`📋 Processing incident report for incident on ${incident.date}`);

    // Create the incident report data structure
    const incidentReport: IncidentReport = {
      incident: {
        date: incident.date || '',
        location: incident.location || '',
        description: incident.description || '',
        caused_by: incident.caused_by || ''
      },
      reporter: {
        first_name: reporter.first_name || '',
        last_name: reporter.last_name || '',
        telephone: reporter.telephone || '',
        email: reporter.email || '',
        postal_address: reporter.postal_address || '',
        police_report: reporter.police_report || '',
        witnesses: reporter.witnesses || ''
      }
    };

    // Generate a unique report ID for tracking
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const reportId = `IR_${timestamp}_${Math.floor(Math.random() * 10000)
      .toString()
      .padStart(4, '0')}`;

    // In a real implementation, you would save this to a database or send to an API
    console.log(`📝 Incident report collected with ID: ${reportId}`);
    console.log('📊 Incident Report Data:', JSON.stringify(incidentReport, null, 2));

    // Prepare response message
    const description = incidentReport.incident.description;
    const truncatedDesc =
      description.length > 100 ? description.slice(0, 100) + '...' : description;

    const result = `Incident report successfully collected and assigned ID: ${reportId}

Incident Details:
- Date: ${incidentReport.incident.date}
- Location: ${incidentReport.incident.location}
- Caused by: ${incidentReport.incident.caused_by}
- Description: ${truncatedDesc}

Reporter Information:
- Name: ${incidentReport.reporter.first_name} ${incidentReport.reporter.last_name}
- Contact: ${incidentReport.reporter.telephone} / ${incidentReport.reporter.email}
- Police Report Filed: ${incidentReport.reporter.police_report}
- Witnesses Present: ${incidentReport.reporter.witnesses}

Report has been logged and will be processed accordingly.`;

    console.log('✅ Incident report processed successfully');

    return {
      success: true,
      data: result
    };
  } catch (error) {
    console.error('❌ Incident report tool error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred during report collection'
    };
  }
};

